import {
  type ReversingDistanceSetting,
  type ReversingDistanceSettingMetadata,
  HfsSettingsActionEnum,
  HfsActionSetting,
  SearchChargingStationSettingMetadata,
  SearchChargingStationValue,
  SearchChargingStationSetting,
  DrivePastWireSetting,
  DrivePastWireSettingMetadata,
  CorridorWidthSettingMetadata,
  CorridorWidthSetting,
  GpsAssistedNavigationEnabledSetting,
  FollowWireStartingPointsSettingMetadata,
  FollowWireType,
  FollowWireStartingPointsSetting,
} from 'fleet-data-model';
import {
  ReversingDistanceTarget,
  SearchChargingStationTarget,
  DrivePastWireTarget,
  CorridorWidthTarget,
  GpsAssistedNavigationEnabledTarget,
  FollowWireStartingPointsTarget,
} from 'fleet-operation-service-contracts';
import { SettingsAction } from '@fleet-bffs/api-contract';
import type { Update } from '~/domain/roboticmower-details/components/RoboticInstallationCard/reducer';
import { HfsOperationResult } from '~/domain/assets/models/HfsOperationResult';
import {
  type CorridorWidthPayload,
  RangeUnitType,
  RoboticInstallationSettings,
  SearchChargingStationPayload,
  SingleRangePayload,
  GpsNavigationPayload,
  LawnGuidancePayload,
} from '~/data/RoboticInstallationSettings';

const guidesNumbers = [1, 2, 3];

const calculateStartingDistance = (startingDistanceAction: SettingsAction | undefined) => {
  if (
    startingDistanceAction?.settingData?.metadata === undefined ||
    startingDistanceAction?.settingData?.value === undefined
  ) {
    return undefined;
  }

  return {
    settingType: 'EXIT_ANGLES',
    titleLangKey: 'bff.robotic_installation_setting.starting_distance.title',
    descriptionLangKey: 'bff.robotic_installation_setting.starting_distance.description',
    min:
      ((startingDistanceAction.settingData.metadata as ReversingDistanceSettingMetadata).range
        ?.min ?? 0) / 10,
    max:
      ((startingDistanceAction.settingData.metadata as ReversingDistanceSettingMetadata).range
        ?.max ?? 0) / 10,
    value: ((startingDistanceAction.settingData as ReversingDistanceSetting).value as number) / 10,
    stepSize: 1,
    units: RangeUnitType.CM,
    disabled: !startingDistanceAction?.valid,
    invalidValue: startingDistanceAction.settingData.invalidValue,
  };
};

const calculateDrivePastWire = (drivePastWireAction: SettingsAction | undefined) => {
  if (
    drivePastWireAction?.settingData?.metadata === undefined ||
    drivePastWireAction?.settingData?.value === undefined
  ) {
    return undefined;
  }
  const settingData = drivePastWireAction?.settingData as DrivePastWireSetting;
  const metadata = settingData.metadata as DrivePastWireSettingMetadata;
  if (metadata.range?.min === undefined || metadata.range?.max === undefined) {
    return undefined;
  }

  return {
    settingType: 'DRIVE_PAST_WIRE',
    titleLangKey: 'bff.robotic_installation_setting.drive_past_wire.title',
    descriptionLangKey: 'bff.robotic_installation_setting.drive_past_wire.description',
    min: metadata.range?.min / 10,
    max: metadata.range?.max / 10,
    value: (settingData.value ?? 0) / 10,
    stepSize: 1,
    units: RangeUnitType.CM,
    disabled: !drivePastWireAction?.valid,
    invalidValue: settingData.invalidValue,
  };
};

const calculateCorridorWidth = (hfsAction: SettingsAction | undefined) => {
  if (
    hfsAction?.settingData?.metadata === undefined ||
    hfsAction?.settingData?.value === undefined
  ) {
    return undefined;
  }
  const settingData = hfsAction?.settingData as CorridorWidthSetting;
  const metadata = settingData.metadata as CorridorWidthSettingMetadata;
  if (
    metadata.range?.min === undefined ||
    metadata.range?.max === undefined ||
    settingData.value?.boundaryWire === undefined
  ) {
    return undefined;
  }

  return {
    settingType: 'CORRIDOR_WIDTH',
    titleLangKey: 'bff.robotic_installation_setting.corridor_width.title',
    descriptionLangKey: 'bff.robotic_installation_setting.corridor_width.description',
    boundary: {
      loopWire: 'BOUNDARY_WIRE',
      min: metadata.range.min,
      max: metadata.range.max,
      value: settingData.value?.boundaryWire,
      stepSize: 1,
      disabled: !hfsAction?.valid,
      invalidValue: settingData.invalidValue,
    },
    guides: guidesNumbers
      .filter((x) => settingData?.value?.[`guide${x}`] !== undefined)
      .map((x) => ({
        loopWire: `GUIDE_${x}`,
        min: metadata.range?.min ?? 0,
        max: metadata.range?.max ?? 0,
        stepSize: 1,
        value: settingData?.value?.[`guide${x}`] ?? 0,
        disabled: !hfsAction?.valid,
        invalidValue: settingData.invalidValue,
      })),
  };
};

const calculateGpsNavigation = (hfsAction: SettingsAction | undefined) => {
  if (hfsAction?.settingData?.value === undefined) {
    return undefined;
  }
  const settingData = hfsAction?.settingData as GpsAssistedNavigationEnabledSetting;

  return {
    settingType: 'GPS_NAVIGATION',
    enabled: !!settingData.value,
    disabled: !hfsAction?.valid,
    invalidValue: settingData.invalidValue,
  };
};

const calculateSearchChargingStation = (searchCsAction: SettingsAction | undefined) => {
  const searchCsActionMetadata =
    searchCsAction?.settingData?.metadata === undefined
      ? undefined
      : (searchCsAction?.settingData?.metadata as SearchChargingStationSettingMetadata);
  const searchCsActionValue =
    searchCsAction?.settingData?.value === undefined
      ? undefined
      : (searchCsAction?.settingData?.value as SearchChargingStationValue);
  if (
    searchCsActionMetadata?.boundaryDelayTimeRange?.min === undefined ||
    searchCsActionMetadata?.boundaryDelayTimeRange?.max === undefined ||
    searchCsActionMetadata?.guideDelayTimeRange?.min === undefined ||
    searchCsActionMetadata?.guideDelayTimeRange?.max === undefined ||
    searchCsActionValue === undefined
  ) {
    return undefined;
  }

  return {
    disabled: !searchCsAction?.valid,
    invalidValue: searchCsAction?.settingData?.invalidValue,
    settingType: 'SEARCH_CHARGING_STATION',
    titleLangKey: 'bff.robotic_installation_setting.search_charging_station.title',
    descriptionLangKey: 'bff.robotic_installation_setting.search_charging_station.description',
    signal: [
      {
        titleLangKey: 'bff.robotic_installation_setting.signal_strength.min',
        value: 'MINIMUM',
        selected: searchCsActionValue.signalRange === 'MINIMUM',
      },
      {
        titleLangKey: 'bff.robotic_installation_setting.signal_strength.med',
        value: 'MEDIUM',
        selected: searchCsActionValue.signalRange === 'MEDIUM',
      },
      {
        titleLangKey: 'bff.robotic_installation_setting.signal_strength.max',
        value: 'MAXIMUM',
        selected: searchCsActionValue.signalRange === 'MAXIMUM',
      },
    ],
    boundary: {
      loopWire: 'BOUNDARY_WIRE',
      min: searchCsActionMetadata.boundaryDelayTimeRange.min / 60,
      max: searchCsActionMetadata.boundaryDelayTimeRange.max / 60,
      stepSize: 1,
      value: (searchCsActionValue.boundaryWire?.delayTime ?? 0) / 60,
      units: RangeUnitType.MIN,
      enabled: !!searchCsActionValue.boundaryWire?.enabled,
    },
    guides: guidesNumbers
      .filter((x) => searchCsActionValue[`guide${x}`]?.enabled !== undefined)
      .map((x) => ({
        loopWire: `GUIDE_${x}`,
        min: (searchCsActionMetadata?.guideDelayTimeRange?.min ?? 0) / 60,
        max: (searchCsActionMetadata?.guideDelayTimeRange?.max ?? 0) / 60,
        stepSize: 1,
        value: (searchCsActionValue[`guide${x}`]?.delayTime ?? 0) / 60,
        units: RangeUnitType.MIN,
        enabled: !!searchCsActionValue[`guide${x}`].enabled,
      })),
  };
};

export const mapLoopWireCode = (type: FollowWireType) => {
  switch (type) {
    case FollowWireType.BOUNDARY_LEFT:
      return 'LEFT_BOUNDARY_WIRE';
    case FollowWireType.BOUNDARY_RIGHT:
      return 'RIGHT_BOUNDARY_WIRE';
    default:
      return type;
  }
};

export const mapWireType = (type: string) => {
  switch (type) {
    case 'LEFT_BOUNDARY_WIRE':
      return FollowWireType.BOUNDARY_LEFT;
    case 'RIGHT_BOUNDARY_WIRE':
      return FollowWireType.BOUNDARY_RIGHT;
    default:
      return type as FollowWireType;
  }
};

export const mapLoopWireTitle = (type: FollowWireType) => {
  switch (type) {
    case FollowWireType.BOUNDARY_LEFT:
      return 'bff.robotic_installation_setting.start_position.boundary_left.title';
    case FollowWireType.BOUNDARY_RIGHT:
      return 'bff.robotic_installation_setting.start_position.boundary_right.title';
    default:
      return `bff.robotic_installation_setting.start_position.guide${type.replace(
        'GUIDE_',
        ''
      )}.title`;
  }
};

export const calculateLoopWires = (hfsAction: SettingsAction | undefined) => {
  if (hfsAction?.settingData === undefined) {
    return [];
  }
  const metadata =
    hfsAction?.settingData?.metadata === undefined
      ? undefined
      : (hfsAction?.settingData?.metadata as FollowWireStartingPointsSettingMetadata);
  return (
    metadata?.wireTypes?.map((wireType) => ({
      value: mapLoopWireCode(wireType),
      titleLangKey: mapLoopWireTitle(wireType),
    })) ?? []
  );
};

export const calculateLawnGuidance = (hfsAction: SettingsAction | undefined) => {
  if (
    hfsAction?.settingData?.metadata === undefined ||
    hfsAction?.settingData?.value === undefined
  ) {
    return undefined;
  }
  const settingData = hfsAction?.settingData as FollowWireStartingPointsSetting;
  const metadata =
    settingData?.metadata === undefined
      ? undefined
      : (settingData?.metadata as FollowWireStartingPointsSettingMetadata);
  const value = settingData?.value;
  if (metadata?.distanceRange?.min === undefined || metadata?.distanceRange?.max === undefined) {
    return undefined;
  }

  return {
    settingType: 'FOLLOW_WIRE_OUT',
    disabled: !hfsAction.valid,
    invalidValue: settingData.invalidValue,
    titleLangKey: 'bff.robotic_installation_setting.lawn_guidance.title',
    descriptionLangKey: 'bff.robotic_installation_setting.lawn_guidance.description',
    startingPoints:
      value?.map((item, index) => ({
        enabled: item.enabled,
        startPositionId: index + 1,
        startPosition: mapLoopWireCode(item.wireType),
        proportion: item.proportion,
        passageCuttingEnabled: item.systematicPassageMowing,
        runningDistance: {
          min: metadata?.distanceRange?.min ?? 0,
          max: metadata?.distanceRange?.max ?? 0,
          stepSize: 1,
          value: item.distance,
          units: RangeUnitType.METERS,
        },
      })) ?? [],
  };
};

export const mapHfsInstallationSettings = (
  hfsActions: SettingsAction[]
): RoboticInstallationSettings => {
  const drivePastWireAction = hfsActions?.find(
    (action) => action.hfsAction === HfsSettingsActionEnum.DRIVE_PAST_WIRE
  );
  const startingDistanceAction = hfsActions?.find(
    (action) => action.hfsAction === HfsSettingsActionEnum.REVERSING_DISTANCE
  );
  const searchCsAction = hfsActions?.find(
    (action) => action.hfsAction === HfsSettingsActionEnum.SEARCH_CHARGING_STATION
  );
  const cwAction = hfsActions?.find(
    (action) => action.hfsAction === HfsSettingsActionEnum.CORRIDOR_WIDTH
  );
  const gpsNavigationAction = hfsActions?.find(
    (action) => action.hfsAction === HfsSettingsActionEnum.GPS_ASSISTED_NAVIGATION_ENABLED
  );
  const fwspAction = hfsActions?.find(
    (action) => action.hfsAction === HfsSettingsActionEnum.FOLLOW_WIRE_STARTING_POINTS
  );

  return {
    drivePastWire: calculateDrivePastWire(drivePastWireAction),
    startingDistance: calculateStartingDistance(startingDistanceAction),
    searchChargingStation: calculateSearchChargingStation(searchCsAction),
    corridorWidth: calculateCorridorWidth(cwAction),
    gpsNavigation: calculateGpsNavigation(gpsNavigationAction),
    lawnGuidance: calculateLawnGuidance(fwspAction),
    loopWires: calculateLoopWires(fwspAction),
  };
};

const mapSearchChargingStationGuideTarget = (
  payload: SearchChargingStationPayload,
  guideNumber: string
) => {
  const guide = payload.guides.find((item) => item.loopWire === `GUIDE_${guideNumber}`);
  return guide?.enabled === undefined || guide?.value === undefined
    ? undefined
    : {
        enabled: guide.enabled,
        delayTime: guide.value * 60,
      };
};

export const mapHfsInstallationSettingUpdateTargets = (
  update: Update
): (
  | ReversingDistanceTarget
  | SearchChargingStationTarget
  | DrivePastWireTarget
  | CorridorWidthTarget
  | GpsAssistedNavigationEnabledTarget
  | FollowWireStartingPointsTarget
)[] => {
  switch (update.payload.code) {
    case 'searchChargingStation':
      const payload = update.payload.payload as SearchChargingStationPayload;
      const guide1 = mapSearchChargingStationGuideTarget(payload, '1');
      const guide2 = mapSearchChargingStationGuideTarget(payload, '2');
      const guide3 = mapSearchChargingStationGuideTarget(payload, '3');
      return [
        {
          hfsActionId: HfsSettingsActionEnum.SEARCH_CHARGING_STATION,
          iprId: update.assetId,
          payload: {
            signalRange: payload.signal,
            boundaryWire:
              payload.boundary === undefined
                ? undefined
                : {
                    enabled: payload.boundary.enabled,
                    delayTime: payload.boundary.value * 60,
                  },
            guide1,
            guide2,
            guide3,
          },
        } as SearchChargingStationTarget,
      ];
    case 'startingDistance':
      return [
        {
          hfsActionId: HfsSettingsActionEnum.REVERSING_DISTANCE,
          iprId: update.assetId,
          payload: {
            reversingDistance: (update.payload.payload as SingleRangePayload).value * 10,
          },
        },
      ];
    case 'gpsNavigation':
      return [
        {
          hfsActionId: HfsSettingsActionEnum.GPS_ASSISTED_NAVIGATION_ENABLED,
          iprId: update.assetId,
          payload: {
            enabled: (update.payload.payload as GpsNavigationPayload).enabled,
          },
        },
      ];
    case 'drivePastWire':
      return [
        {
          hfsActionId: HfsSettingsActionEnum.DRIVE_PAST_WIRE,
          iprId: update.assetId,
          payload: {
            drivePastWire: (update.payload.payload as SingleRangePayload).value * 10,
          },
        },
      ];
    case 'corridorWidth':
      const cwPayload = update.payload.payload as CorridorWidthPayload;
      const target: CorridorWidthTarget = {
        hfsActionId: HfsSettingsActionEnum.CORRIDOR_WIDTH,
        iprId: update.assetId,
        payload: {
          boundaryWire: cwPayload.boundary,
        },
      };
      cwPayload.guides.forEach((guide) => {
        target.payload[`guide${guide.loopWire.replace('GUIDE_', '')}`] = guide.value;
      });

      return [target];
    case 'lawnGuidance':
      return [
        {
          hfsActionId: HfsSettingsActionEnum.FOLLOW_WIRE_STARTING_POINTS,
          iprId: update.assetId,
          payload: {
            startingPoints: (update.payload.payload as LawnGuidancePayload).startingPoints.map(
              (item) => ({
                enabled: item.enabled,
                wireType: mapWireType(item.startPosition),
                distance: item.runningDistance,
                proportion: item.proportion,
                systematicPassageMowing: item.passageCuttingEnabled,
              })
            ),
          },
        },
      ];
    default:
      return [];
  }
};

export const getHfsInstallationActionSettingDataUpdates = (
  operation: HfsOperationResult
): HfsActionSetting | undefined => {
  if (operation?.payload !== undefined) {
    const hfsActionId = operation.hfsActionId as unknown as HfsSettingsActionEnum;
    switch (hfsActionId) {
      case HfsSettingsActionEnum.REVERSING_DISTANCE:
        return {
          value: (operation.payload as ReversingDistanceTarget['payload']).reversingDistance,
        } as ReversingDistanceSetting;
      case HfsSettingsActionEnum.DRIVE_PAST_WIRE:
        return {
          value: (operation.payload as DrivePastWireTarget['payload']).drivePastWire,
        } as DrivePastWireSetting;
      case HfsSettingsActionEnum.GPS_ASSISTED_NAVIGATION_ENABLED:
        return {
          value: (operation.payload as GpsAssistedNavigationEnabledTarget['payload']).enabled,
        } as GpsAssistedNavigationEnabledSetting;
      case HfsSettingsActionEnum.SEARCH_CHARGING_STATION:
        const scsPayload = operation.payload as SearchChargingStationTarget['payload'];
        const setting = {
          value: {
            signalRange: scsPayload.signalRange,
            boundaryWire:
              scsPayload?.boundaryWire?.enabled === undefined ||
              scsPayload?.boundaryWire?.delayTime === undefined
                ? undefined
                : {
                    enabled: scsPayload.boundaryWire.enabled,
                    delayTime: scsPayload.boundaryWire.delayTime,
                  },
          },
        };
        guidesNumbers.forEach((guideNumber) => {
          if (
            scsPayload?.[`guide${guideNumber}`]?.enabled !== undefined &&
            scsPayload?.[`guide${guideNumber}`]?.delayTime !== undefined
          ) {
            setting.value[`guide${guideNumber}`] = {
              enabled: scsPayload[`guide${guideNumber}`].enabled,
              delayTime: scsPayload[`guide${guideNumber}`].delayTime,
            };
          }
        });
        return setting as SearchChargingStationSetting;
      case HfsSettingsActionEnum.FOLLOW_WIRE_STARTING_POINTS:
        return {
          value: (operation.payload as FollowWireStartingPointsTarget['payload']).startingPoints,
        } as FollowWireStartingPointsSetting;
      case HfsSettingsActionEnum.CORRIDOR_WIDTH:
        const cwPayload = operation.payload as CorridorWidthTarget['payload'];
        const cwSetting = {
          value: {
            boundaryWire: cwPayload.boundaryWire,
          },
        };
        guidesNumbers.forEach((guideNumber) => {
          if (cwPayload?.[`guide${guideNumber}`] !== undefined) {
            cwSetting.value[`guide${guideNumber}`] = cwPayload?.[`guide${guideNumber}`];
          }
        });
        return cwSetting as CorridorWidthSetting;
    }
  }
  return undefined;
};
